import {
  RichText, RichTextData, RichTextLeaf, TextTrackSprite, TrackSprite, UInt, RichTextStyledLeaf, DocStyledText,
  DocReferenceStyledText
} from 'powtoon-versioning/src/currentSchema'
export type RichTextLeafAttributeValues = string | boolean | null | undefined | number
export type RichTextElementTypeValues =  'paragraph' | 'numbered-list' | 'bulleted-list' |'list-item-text'| 'list-item'
export const RichTextElementTypeValuesArray =
    [ 'paragraph', 'numbered-list', 'bulleted-list', 'list-item-text', 'list-item' ]
export type RichTextElementAttributeValues =  RichTextElementTypeValues | RichTextLeaf[]| number| null | undefined
export type RichTextTypes = RichText[] | RichText | RichTextData | string | RichTextLeaf | RichTextLeaf[] | undefined
export type SpriteTypes = TextTrackSprite | TrackSprite
const DEFAULT_H1_LINE_HEIGHT = 1.2
const DEFAULT_H1_LETTER_SPACING = -0.01
const DEFAULT_H2_LINE_HEIGHT = 1.37
const DEFAULT_H2_LETTER_SPACING = 0
const DEFAULT_PARAGRAPH_LINE_HEIGHT = 1.66
const DEFAULT_PARAGRAPH_LETTER_SPACING = -0.01

const TEXT_STYLE_TO_RICH_TEXT_STYLE = {
  h1: {
    lineHeight: DEFAULT_H1_LINE_HEIGHT,
    letterSpacing: DEFAULT_H1_LETTER_SPACING
  },
  h2: {
    lineHeight: DEFAULT_H2_LINE_HEIGHT,
    letterSpacing: DEFAULT_H2_LETTER_SPACING
  },
  paragraph: {
    lineHeight: DEFAULT_PARAGRAPH_LINE_HEIGHT,
    letterSpacing: DEFAULT_PARAGRAPH_LETTER_SPACING
  }
}

export type TextFromLib = {
    text: string,
    fontStyle: 'normal' | 'italic',
    fontWeight: 'normal' | 'bold',
    fontSize: UInt,
    fontName: string,
    textAlign: 'left' | 'center' | 'right'
    textDecoration: 'none' | 'underline'
    textStyle: 'h1' | 'h2' | 'paragraph'
}
export const DEFAULT_RICH_TEXT_COLOR = '#000000'
export const isDocText = (input: SpriteTypes | TextFromLib): input is TextTrackSprite =>
  !!isString((input as TextTrackSprite).text)
export const isDocReferencedText = (input: SpriteTypes | TextFromLib): input is DocReferenceStyledText =>
  isDocText(input) && ('styleRef' in input)
export const isDocStyledText = (input: SpriteTypes | TextFromLib): input is DocStyledText =>
  isDocText(input) && ('fontName' in input)

export const isDocTextAnyValue = (input: SpriteTypes):  input is TextTrackSprite =>
  !!(input as TextTrackSprite).text

export const isRichTextTypes = (input: unknown):  input is RichTextTypes =>
  isRichTextData(input) ||
  isRichText(input) ||
  isRichTextLeaf(input) ||
  isString(input)

export const isRichTextData = (input: unknown):  input is RichTextData => !!(input as RichTextData)?.version
export const isRichText = (node: unknown):  node is RichText => !!(node as RichText).children
export const isRichTextLeaf = (node: unknown):  node is RichTextLeaf => !!isString((node as RichTextLeaf).text)
export const isRichTextStyledLeaf = (node: unknown):  node is RichTextStyledLeaf =>
  isRichTextLeaf(node) && !('styleRef' in node)
export const isString = (input: unknown):  input is string => typeof input === 'string'

export function getFirstRichTextLeaf(value: RichTextLeaf[] | RichText[] | undefined): RichTextLeaf | undefined {
  const richText = value?.[0]
  if (richText) {
    if (isRichText(richText)) {
      return getFirstRichTextLeaf(richText.children)
    }
    if (isRichTextLeaf(richText)) {
      return richText
    }
  }
  return undefined
}

export function getTextSpriteStyleFromRichText(
  value: RichTextStyledLeaf[] | RichText[] | undefined
): Partial<DocStyledText> {
  const richTextLeaf = getFirstRichTextLeaf(value)
  return richTextLeaf && isRichTextStyledLeaf(richTextLeaf) ?
    {
      colors: [richTextLeaf.color],
      fontStyle: richTextLeaf.italic ? 'italic' : 'normal',
      fontWeight: richTextLeaf.bold ? 'bold' : 'normal',
      textDecoration: richTextLeaf.underline ? 'underline' : 'none'
    } :
    {}
}

export function isValidTextSpriteStyle(sprite: DocStyledText): boolean {
  const textStyle = getTextSpriteStyleFromRichText(sprite.richText?.value)
  return (!textStyle.colors || (
    (textStyle.fontStyle === sprite.fontStyle) &&
    (textStyle.fontWeight === sprite.fontWeight) &&
    (textStyle.textDecoration === sprite.textDecoration) &&
    (textStyle.colors[0] === sprite.colors?.[0])
  ))
}

export function convertRichTextToString  (node: RichText[] | RichText | RichTextLeaf | undefined): string {
  if (!node) {
    return ''
  }
  else if (Array.isArray(node)) {
    return node.map(convertRichTextToString).join('\n')
  } else if (isRichText(node) ) {
    return node.children.map(convertRichTextToString).join('')
  } else {
    return node.text
  }
}
export const convertToRichTextFormat = (sprite: DocStyledText | TextFromLib): RichTextData => {
  const {fontStyle, fontWeight, textDecoration, text, textStyle} = sprite
  const additionalStyles = textStyle ? TEXT_STYLE_TO_RICH_TEXT_STYLE[textStyle] : {}
  return {
    version: '1.0.0',
    value: [
      {
        type: 'paragraph',
        children: [{
          text: text.replace('\r', ''),
          bold: fontWeight === 'bold',
          underline: textDecoration === 'underline',
          italic: fontStyle === 'italic',
          color: isDocText(sprite) ?
            (sprite.colors ? sprite.colors[0] : DEFAULT_RICH_TEXT_COLOR) :
            DEFAULT_RICH_TEXT_COLOR
        }],
        ...additionalStyles
      }
    ]
  } as RichTextData
}

export function applyFunctionOnAllLeafs(
  value: RichTextTypes,
  func: (value: RichTextLeaf) => void
) {
  if (value) {
    if (isRichText(value)) {
      value.children.forEach(child => applyFunctionOnAllLeafs(child, func))
    }
    else if (Array.isArray(value)) {
      Object.entries(value).forEach(([, val]) => {
        applyFunctionOnAllLeafs(val, func)
      })
    }
    else if (isRichTextLeaf(value)) {
      func(value)
    }
  }
}
